import type { OrderEntity, OrderNoteEntity } from '../entity';
import type { OrderDetailEmailTemplateDataInterface } from '../interfaces';
import type { ValidationIssue } from '../types/order-preferences.types';
import { EmailEntityDto } from './email.dto';

export interface OrderPaginationDto {
	total: number;
	page: number;
	limit: number;
	totalPages: number;
}

export interface QuotesClientDto {
	_id: number;
	name: string;
	[key: string]: unknown;
}

export interface GetQuotesClientsSuccessResponseDto {
	success: boolean;
	data: Array<QuotesClientDto>;
}

export interface OrdersErrorResponseDto {
	success: boolean;
	error: string;
}

export type GetQuotesClientsResponseDto =
	| GetQuotesClientsSuccessResponseDto
	| OrdersErrorResponseDto;

export interface GetOrdersSuccessResponseDto {
	success: boolean;
	data: Array<OrderEntity>;
	pagination: OrderPaginationDto;
}

export type GetOrdersResponseDto =
	| GetOrdersSuccessResponseDto
	| OrdersErrorResponseDto;

export interface GetOrderSuccessResponseDto {
	success: boolean;
	data: OrderEntity & {
		// PD-14234: server-computed approval capability for the CURRENT
		// viewer, present only when the order is in Approval Required.
		// Union of the blanket orders:approve/orders:reject actions and the
		// per-check approver-role mapping — the frontend must gate the
		// Approve/Reject actions on these rather than the blanket
		// permission alone.
		viewerCanApprove?: boolean;
		viewerCanReject?: boolean;
		// PD-15216: whether this order's status permits cancellation for this
		// tenant, from the same rule the cancel endpoint enforces
		// (Workd-as-ERP tenants may cancel APPROVED orders). The frontend
		// shows the Cancel action from this flag; RBAC stays client-gated.
		cancellable?: boolean;
	};
}

export type GetOrderResponseDto =
	| GetOrderSuccessResponseDto
	| OrdersErrorResponseDto;

export interface CreateOrderSuccessResponseDto {
	success: boolean;
	data: OrderEntity;
}

export type CreateOrderResponseDto =
	| CreateOrderSuccessResponseDto
	| OrdersErrorResponseDto;

export interface UpdateOrderSuccessResponseDto {
	success: boolean;
	data: OrderEntity;
}

export type UpdateOrderResponseDto =
	| UpdateOrderSuccessResponseDto
	| OrdersErrorResponseDto;

export interface DeleteOrderSuccessResponseDto {
	success: boolean;
}

export type DeleteOrderResponseDto =
	| DeleteOrderSuccessResponseDto
	| OrdersErrorResponseDto;

export interface OrderDetailDto {
	_id: number;
	_fk_order__id: number;
	lineNumber: number | null;
	_fk_item__id: number | null;
	price: string | null;
	quantityOrdered: number;
	quantityShipped: number | null;
	quantityBackordered: number | null;
	// Per-client available stock for this line's item, scoped to the order
	// client's warehouses (see OrdersService.getOrderDetails). Optional "Qty In
	// Stock" line-items column.
	quantityOnHand?: number | null;
	subtotal: string | null;
	freight: string | null;
	tax: string | null;
	discount: string | null;
	total: string | null;
	acquisitionCost: string | null;
	totalGP: string | null;
	minimumPrice: string | null;
	externalId: string | null;
	description: string | null;
	itemExternalId?: string | null;
	itemExternalId2?: string | null;
	manufacturer?: string | null;
	itemNDC?: string | null;
	itemUnitOfMeasure?: string | null;
	itemStrength?: string | null;
	sizeCount?: string | null;
	strength?: string | null;
	size?: string | null;
	itemCategory?: string | null;
	itemSubcategory?: string | null;
	itemDescription?: string | null;
	itemManufacturer?: string | null;
	itemPrice?: string | null;
	itemMinimumPrice?: string | null;
}

export interface GetOrderDetailsSuccessResponseDto {
	success: boolean;
	data: Array<OrderDetailDto>;
}

export type GetOrderDetailsResponseDto =
	| GetOrderDetailsSuccessResponseDto
	| OrdersErrorResponseDto;

export interface OrderNoteModel extends OrderNoteEntity {
	_id: number;
	_fk_order__id: number;
	_created_date: Date;
	_modified_date: Date;
	_deleted_date: Date | undefined;
	ownerUserFirstName: string | null;
	ownerUserLastName: string | null;
	ownerUserFullName: string | null;
}

export interface GetOrderNotesSuccessResponseDto {
	success: boolean;
	data: Array<OrderNoteModel>;
}

export type GetOrderNotesResponseDto =
	| GetOrderNotesSuccessResponseDto
	| OrdersErrorResponseDto;

export interface CreateOrderNoteSuccessResponseDto {
	success: boolean;
	data: OrderNoteEntity;
}

export type CreateOrderNoteResponseDto =
	| CreateOrderNoteSuccessResponseDto
	| OrdersErrorResponseDto;

export interface DeleteOrderNoteSuccessResponseDto {
	success: boolean;
}

export type DeleteOrderNoteResponseDto =
	| DeleteOrderNoteSuccessResponseDto
	| OrdersErrorResponseDto;

export interface RecoverOrderNoteSuccessResponseDto {
	success: boolean;
	data: OrderNoteEntity;
}

export type RecoverOrderNoteResponseDto =
	| RecoverOrderNoteSuccessResponseDto
	| OrdersErrorResponseDto;

export interface SubmitOrderSuccessResponseDto {
	success: boolean;
	data: OrderEntity;
}

export type SubmitOrderResponseDto =
	| SubmitOrderSuccessResponseDto
	| OrdersErrorResponseDto;

/**
 * Error code on the 409 a Place Order receives while another submit of the
 * same order still holds the per-order submit lock (PD-14907). Distinct from
 * the license good-standing 409 (`LicenseGoodStandingAckRequired`) so the
 * client can tell "already being placed" from "refused" (PD-16731).
 */
export const ORDER_SUBMIT_IN_PROGRESS_ERROR = 'OrderSubmitInProgress';

/**
 * PD-16731 — whether a submitted cart actually became an order.
 *
 * The server keeps placing an order after the browser stops waiting for the
 * response, so a Place Order that timed out, lost its connection or hit the
 * submit lock asks this before telling the rep anything.
 */
export type OrderSubmitStatusDto = Pick<
	OrderEntity,
	'id' | 'status' | 'externalId'
> & {
	/** True while the order is still a cart (status Unsubmitted). */
	isCart: boolean;
	/** True while a submit of this order holds the per-order submit lock. */
	submitInProgress: boolean;
};

export interface OrderSubmitStatusResponseDto {
	success: boolean;
	data: OrderSubmitStatusDto;
}

/**
 * PD-16731 — an order placed recently for the same client with the same items
 * and the same total quantity of each, found before a cart is placed so the
 * person placing it can be asked whether they mean to place it again. Called
 * "matching" rather than "duplicate" because Duplicate Order already means
 * copying an order into a new cart.
 */
export type RecentMatchingOrderDto = Pick<
	OrderEntity,
	'externalId' | 'externalId2' | 'total'
> & {
	orderId: OrderEntity['id'];
	/**
	 * When the earlier order was placed, as an ISO string: the entity column
	 * is a Date, but it crosses the wire as a string.
	 */
	date: string | null;
};

export interface RecentMatchingOrderResponseDto {
	success: boolean;
	data: RecentMatchingOrderDto | null;
}

export interface DuplicateOrderSuccessResponseDto {
	success: boolean;
	data: OrderEntity;
}

export type DuplicateOrderResponseDto =
	| DuplicateOrderSuccessResponseDto
	| OrdersErrorResponseDto;

/** Legacy shipping method from _object_field_select_options */
export interface LegacyShippingMethodDto {
	value: string;
	text: string;
	subtext?: string;
}

/** New configurable shipping method from shipping_method table */
export interface ShippingMethodDto {
	id: number;
	label: string;
	description?: string | null;
	fee: string;
	freeShippingThreshold?: string | null;
	externalId?: string | null;
	isAvailableInCrm: boolean;
	isAvailableInEcommerce: boolean;
	isActive: boolean;
	hideWhenNotAllowed?: boolean;
	sortOrder?: number | null;
	companies?: Array<{ id: number; name: string }>;
	restrictions?: Array<{
		id: number;
		restrictionType: string;
		criteriaValue: string;
		onlyWhenPresent?: boolean;
	}>;
}

/** Shipping method with computed fields for a specific order context */
export interface ShippingMethodForOrderDto extends ShippingMethodDto {
	/** The effective price based on order subtotal vs free shipping threshold */
	effectivePrice: string;
	/** Whether this method is allowed based on all restriction checks */
	isAllowed: boolean;
	/** Whether this method is disabled (fails restriction checks) */
	isDisabled: boolean;
	/** Reason the method is disabled, if applicable */
	disabledReason?: string;
	/** Additional fees from client/item allowedShippingMethods surcharges */
	additionalFee?: string;
	/**
	 * PD-15795 — the subtotal the server actually compared against this
	 * method's free-shipping threshold: the order's line subtotals minus any
	 * lines carrying an item this method excludes. Differs per method because
	 * the exclusion list is per method. Equal to the plain lines subtotal when
	 * the method excludes nothing. Display mirrors should prefer this over the
	 * raw order subtotal so their preview matches the server's decision.
	 */
	thresholdSubtotal: string;
}

export interface GetShippingMethodsSuccessResponseDto {
	success: boolean;
	data: Array<ShippingMethodDto>;
}

export interface GetShippingMethodsForOrderSuccessResponseDto {
	success: boolean;
	/** Whether this is using the new configurable shipping methods */
	isConfigured: boolean;
	data: Array<ShippingMethodForOrderDto>;
	/** Legacy methods returned when no new methods are configured */
	legacyData?: Array<LegacyShippingMethodDto>;
	/** Per-item surcharge labels keyed by item ID, for display on cart items */
	itemSurcharges?: Record<number, string>;
	/** True when no shipping method can satisfy all items (conflicting restrictions) */
	hasConflict?: boolean;
	/** Message describing the conflict */
	conflictMessage?: string;
}

export type GetShippingMethodsResponseDto =
	| GetShippingMethodsSuccessResponseDto
	| OrdersErrorResponseDto;

export interface CartOrderDetailDto {
	_id: number;
	_fk_order__id: number;
	lineNumber: number | null;
	_fk_item__id: number | null;
	price: string | null;
	quantityOrdered: number;
	quantityShipped: number | null;
	subtotal: string | null;
	freight: string | null;
	tax: string | null;
	discount: string | null;
	total: string | null;
	acquisitionCost: string | null;
	totalGP: string | null;
	minimumPrice: string | null;
	itemDescription?: string | null;
	itemNDC?: string | null;
	itemManufacturer?: string | null;
	itemExternalId?: string | null;
	itemExternalId2?: string | null;
	totalStock?: number;
	quantityOnOrder?: number;
	soonestExpirationDate?: string | null;
	soonestArrivalDate?: string | null;
	itemIsActive?: boolean | number;
}

export interface OrderCartDto extends Omit<OrderEntity, 'orderDetails'> {
	orderDetails: Array<CartOrderDetailDto>;
	billingAddressId?: number | null;
	shippingAddressId?: number | null;
	_fk_client_address__id_billing?: number | null;
	_fk_client_address__id_shipping?: number | null;
	// PD-15423 — cross-account delivery destination (snake-case mirror of
	// OrderEntity.fkClientIdDelivery for the cart UI).
	fk_client__id_delivery?: number | null;
	processingFee?: string | number | null;
	processingFeeType?: string | null;
	paymentMethodTypeId?: number | null;
}

export interface GetCartSuccessResponseDto {
	success: boolean;
	data: OrderCartDto | null;
}

export type GetCartResponseDto =
	| GetCartSuccessResponseDto
	| OrdersErrorResponseDto;

/**
 * PD-16171 — the unpriced lines in a client's cart, as the `itemNoPrice`
 * issues submit would refuse with (GET /orders/cart/:clientId/unpriced-lines).
 */
export interface GetCartUnpricedLinesSuccessResponseDto {
	success: boolean;
	data: Array<ValidationIssue>;
}

export type GetCartUnpricedLinesResponseDto =
	| GetCartUnpricedLinesSuccessResponseDto
	| OrdersErrorResponseDto;

export interface AddToCartSuccessResponseDto {
	success: boolean;
	data: OrderCartDto | null;
}

export type AddToCartResponseDto =
	| AddToCartSuccessResponseDto
	| OrdersErrorResponseDto;

export interface UpdateCartItemSuccessResponseDto {
	success: boolean;
	data: OrderCartDto | null;
}

export type UpdateCartItemResponseDto =
	| UpdateCartItemSuccessResponseDto
	| OrdersErrorResponseDto;

export interface RemoveFromCartSuccessResponseDto {
	success: boolean;
	data: OrderCartDto | null;
}

export type RemoveFromCartResponseDto =
	| RemoveFromCartSuccessResponseDto
	| OrdersErrorResponseDto;

export interface ClearCartSuccessResponseDto {
	success: boolean;
	message?: string;
}

export type ClearCartResponseDto =
	| ClearCartSuccessResponseDto
	| OrdersErrorResponseDto;

// PD-15168 — the fulfil response contracts, and the raw invoice shape they
// carried, were removed with `POST /orders/:id/fulfill`. Fulfilment is no longer
// an HTTP surface of its own: it is reached from order submission's deliver-now
// branch, whose response shape is the submit response.

export interface BatchCartOperationSuccessResponseDto {
	success: boolean;
	data: OrderCartDto | null;
}

export type BatchCartOperationResponseDto =
	| BatchCartOperationSuccessResponseDto
	| OrdersErrorResponseDto;

/**
 * A reorder line that was not added to the cart (PD-16171), so the screen can
 * name it instead of silently dropping it.
 */
export interface ReorderSkippedItemDto {
	itemId: number;
	description: string | null;
	/**
	 * `noPrice`: the item has no price for the client at that quantity.
	 * `unavailable`: the item can no longer be ordered (inactive, a
	 * product-level row, or not sold online).
	 */
	reason: 'noPrice' | 'unavailable';
	/** One readable sentence naming the item and why it was skipped. */
	message: string;
}

export interface ReorderResultDto {
	cartId: number;
	clientId: number;
	itemsAdded: number;
	totalSubtotal: number;
	/** Lines left out of the cart; empty when every line was added. */
	skippedItems: Array<ReorderSkippedItemDto>;
}

export interface ReorderSuccessResponseDto {
	success: boolean;
	data: ReorderResultDto;
}

export type ReorderResponseDto =
	| ReorderSuccessResponseDto
	| OrdersErrorResponseDto;

export interface OrderStatusCountDto {
	id: string;
	label: string;
	count: number;
}

export interface GetStatusCountsSuccessResponseDto {
	success: boolean;
	data: Array<OrderStatusCountDto>;
}

export type GetStatusCountsResponseDto =
	| GetStatusCountsSuccessResponseDto
	| OrdersErrorResponseDto;

export interface DistinctOrderStatusDto {
	id: string;
	label: string;
	count: number;
}

export interface GetDistinctOrderStatusesSuccessResponseDto {
	success: boolean;
	data: Array<DistinctOrderStatusDto>;
}

export type GetDistinctOrderStatusesResponseDto =
	| GetDistinctOrderStatusesSuccessResponseDto
	| OrdersErrorResponseDto;

export interface AvailablePaymentMethodDto {
	id: number;
	paymentMethodType: string;
	processingFeeType: string | null;
	processingFee: number;
}

export interface GetAvailablePaymentMethodsSuccessResponseDto {
	success: boolean;
	data: Array<AvailablePaymentMethodDto>;
	message?: string;
}

export type GetAvailablePaymentMethodsResponseDto =
	| GetAvailablePaymentMethodsSuccessResponseDto
	| OrdersErrorResponseDto;

export interface ApproveOrderSuccessResponseDto {
	success: boolean;
	data: OrderEntity;
}

export type ApproveOrderResponseDto =
	| ApproveOrderSuccessResponseDto
	| OrdersErrorResponseDto;

export interface UpdateOrderStatusSuccessResponseDto {
	success: boolean;
	data: OrderEntity;
}

export type UpdateOrderStatusResponseDto =
	| UpdateOrderStatusSuccessResponseDto
	| OrdersErrorResponseDto;

/**
 * PD-15103 — the outcome of handing one payment back.
 *
 * Cancelling an order can carry several payments, each reversed by a different
 * mechanism and each able to fail on its own. Reporting them individually is
 * what lets a caller say "the account funds came back but the card hold did
 * not" instead of reporting a single success that is only partly true.
 */
export interface OrderPaymentReversalResult {
	transactionId: number;
	mechanism: 'Void' | 'Refund';
	amount: number;
	paymentMethodType: string;
	succeeded: boolean;
	// Why it failed, or which transaction recorded the reversal when it worked.
	detail: string;
}

export interface OrderPaymentReversalReport {
	orderId: number;
	attempted: number;
	succeeded: number;
	failed: number;
	results: Array<OrderPaymentReversalResult>;
}

export interface CancelOrderSuccessResponseDto {
	success: boolean;
	data: OrderEntity;
	// Absent when the order carried no payment. `failed > 0` means money is
	// still standing against a cancelled order and someone has to act on it.
	paymentReversal?: OrderPaymentReversalReport;
}

export type CancelOrderResponseDto =
	| CancelOrderSuccessResponseDto
	| OrdersErrorResponseDto;

export interface RejectOrderSuccessResponseDto {
	success: boolean;
	data: OrderEntity;
}

export type RejectOrderResponseDto =
	| RejectOrderSuccessResponseDto
	| OrdersErrorResponseDto;

export interface ResubmitOrderSuccessResponseDto {
	success: boolean;
	data: OrderEntity;
}

export type ResubmitOrderResponseDto =
	| ResubmitOrderSuccessResponseDto
	| OrdersErrorResponseDto;

export interface ReviseOrderSuccessResponseDto {
	success: boolean;
	data: OrderCartDto | null;
}

export type ReviseOrderResponseDto =
	| ReviseOrderSuccessResponseDto
	| OrdersErrorResponseDto;

export interface ReconcileRevisionSuccessResponseDto {
	success: boolean;
	data: OrderCartDto | null;
}

export type ReconcileRevisionResponseDto =
	| ReconcileRevisionSuccessResponseDto
	| OrdersErrorResponseDto;

export interface ForceRevisionSuccessResponseDto {
	success: boolean;
	data: OrderCartDto | null;
}

export type ForceRevisionResponseDto =
	| ForceRevisionSuccessResponseDto
	| OrdersErrorResponseDto;

export interface OrderPaymentDto {
	id: number;
	amount: number;
	paymentDate?: string;
	method?: string;
	[key: string]: unknown;
}

export interface GetOrderPaymentsSuccessResponseDto {
	success: boolean;
	data: Array<OrderPaymentDto>;
}

export type GetOrderPaymentsResponseDto =
	| GetOrderPaymentsSuccessResponseDto
	| OrdersErrorResponseDto;

export interface OrderInvoiceDto {
	_id: number;
	externalId?: string | null;
	date?: string | null;
	type?: string | null;
	status?: string | null;
	total?: string | null;
	subtotal?: string | null;
	tax?: string | null;
	freight?: string | null;
	[key: string]: unknown;
}

export interface GetOrderInvoicesSuccessResponseDto {
	success: boolean;
	data: Array<OrderInvoiceDto>;
}

export type GetOrderInvoicesResponseDto =
	| GetOrderInvoicesSuccessResponseDto
	| OrdersErrorResponseDto;

export interface UpdateOrderNoteSuccessResponseDto {
	success: boolean;
	data: OrderNoteEntity;
}

export type UpdateOrderNoteResponseDto =
	| UpdateOrderNoteSuccessResponseDto
	| OrdersErrorResponseDto;

export class EmailOrderDto extends EmailEntityDto<OrderDetailEmailTemplateDataInterface> {}

/**
 * Delivery information for an order (PD-14385) — the trip stop the order
 * was delivered on (`order.fk_trip_stop__id`, maintained by dispatch),
 * shaped for the order-detail Delivery card so support can pull up what
 * happened at the door without digging through trips. Null-yielding
 * endpoint when the order was never dispatched.
 */
export interface OrderDeliveryPhotoDto {
	id: number;
	imageUrl: string | null;
	photoType: string;
	takenAt: string;
	notes: string | null;
}

export interface OrderDeliveryItemDto {
	description: string | null;
	quantityOrdered: number;
	quantityDelivered: number;
	/** Driver-entered reason when delivered < ordered. */
	reason: string | null;
}

export interface OrderDeliveryDto {
	tripId: number;
	tripName: string | null;
	tripStopId: number;
	stopSequence: number;
	stopStatus: string | null;
	arrivalTime: string | null;
	departureTime: string | null;
	driverName: string | null;
	gpsValidated: boolean;
	customerRefused: boolean;
	driverNotes: string | null;
	/** Data-URL signature captured at the door, when one was taken. */
	signatureData: string | null;
	signatureTimestamp: string | null;
	photos: Array<OrderDeliveryPhotoDto>;
	items: Array<OrderDeliveryItemDto>;
}

export interface GetOrderDeliveryResponseDto {
	success: boolean;
	/** Null when the order has no linked trip stop (never dispatched). */
	data?: OrderDeliveryDto | null;
	error?: string;
}
